/**
 * The corpus browser's filter and page.
 *
 * This is the filtering half of what `queue.svelte.ts` used to be. The split is by question:
 * the browser answers "show me records like this" — including records nobody can annotate,
 * which is often exactly what you are looking for — and the work queue answers "what should I
 * do next", which is a question about ordering rather than filtering.
 *
 * **No progress meters here, deliberately.** A count over an arbitrary filter is not progress
 * toward anything. Filter to "unannotated" and a scoped completion bar reads empty however
 * much work you do, because you filtered to the work that is not done. Progress belongs to a
 * task and lives on its card. The corpus *census* — how many records exist, how many the
 * catalog blocked and why — is a different number again, and `GET /api/stats` already has it.
 *
 * Unlike the work queue, this defaults to **no filter at all**. A browser opens on everything;
 * narrowing is the reader's move. The old default hid 3,528 of 3,588 records behind a
 * `seed batch only` checkbox that most people never noticed was ticked.
 */

import { api } from '../api/client';
import { asProblem, type ApiProblem } from '../api/problem';
import type { InscriptionPage, InscriptionQuery, InscriptionSummary } from '../api/types';

export interface BrowseFilter {
  status?: InscriptionQuery['status'];
  siglumPrefix?: string;
  split?: InscriptionQuery['split'];
  minLetters?: number;
  maxLetters?: number;
  /** Tri-state: undefined asks for both, so the browser can find what the queue cannot show. */
  seedBatch?: boolean;
  hasImages?: boolean;
  queueEligible?: boolean;
}

export const DEFAULT_BROWSE_FILTER: BrowseFilter = {};

const PAGE_SIZE = 50;

class BrowseState {
  filter = $state<BrowseFilter>({ ...DEFAULT_BROWSE_FILTER });
  page = $state<InscriptionPage | null>(null);
  offset = $state(0);
  loading = $state(false);
  problem = $state<ApiProblem | null>(null);

  get items(): InscriptionSummary[] {
    return this.page?.items ?? [];
  }

  get total(): number {
    return this.page?.total ?? 0;
  }

  get pageSize(): number {
    return PAGE_SIZE;
  }

  /** True when the reader has narrowed anything — the empty state says different things. */
  get filtered(): boolean {
    return Object.values(this.filter).some((value) => value !== undefined && value !== '');
  }

  toQuery(): InscriptionQuery {
    const query: InscriptionQuery = { limit: PAGE_SIZE, offset: this.offset };
    if (this.filter.status) query.status = this.filter.status;
    if (this.filter.siglumPrefix) query.siglum_prefix = this.filter.siglumPrefix;
    if (this.filter.split) query.split = this.filter.split;
    if (this.filter.minLetters !== undefined) query.min_letters = this.filter.minLetters;
    if (this.filter.maxLetters !== undefined) query.max_letters = this.filter.maxLetters;
    // Tri-state, so `false` is sent rather than dropped: `queue_eligible=false` selects the
    // records the catalog blocked, which is a question only this screen can ask.
    if (this.filter.seedBatch !== undefined) query.seed_batch = this.filter.seedBatch;
    if (this.filter.hasImages !== undefined) query.has_images = this.filter.hasImages;
    if (this.filter.queueEligible !== undefined) query.queue_eligible = this.filter.queueEligible;
    return query;
  }

  async load(): Promise<void> {
    this.loading = true;
    this.problem = null;
    try {
      this.page = await api.inscriptions(this.toQuery());
    } catch (error) {
      this.problem = asProblem(error);
    } finally {
      this.loading = false;
    }
  }

  async ensure(): Promise<void> {
    if (this.page || this.loading) return;
    await this.load();
  }

  async setFilter(next: Partial<BrowseFilter>): Promise<void> {
    this.filter = { ...this.filter, ...next };
    this.offset = 0;
    await this.load();
  }

  async clearFilter(): Promise<void> {
    this.filter = { ...DEFAULT_BROWSE_FILTER };
    this.offset = 0;
    await this.load();
  }

  async goTo(offset: number): Promise<void> {
    this.offset = Math.max(0, offset);
    await this.load();
  }
}

export const browse = new BrowseState();
